import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { LiveSession, LeaderboardState, LeaderboardEntry, RosterEntry } from '@gymboard/types'
import QrDisplay from '../components/qr/QrDisplay'
import ScreenBadge from '../components/ScreenBadge'

interface Props {
  endedSession: LiveSession | null
  endedLeaderboard: LeaderboardState | null
  /** When true the left panel shows a "Live" badge instead of "Final" */
  isLive?: boolean

  // ── Normal mode: right panel shows the upcoming PRESTART session + QR ──────
  upcomingSession?: LiveSession | null
  upcomingQrToken?: string | null
  upcomingQrSignature?: string | null
  upcomingQrExpiresAt?: number | null
  upcomingRoster?: RosterEntry[]

  // ── Handoff mode: right panel shows the new session's live leaderboard ──────
  /** The newly-started (LIVE) session — triggers handoff layout when provided */
  liveSession?: LiveSession | null
  /** Live leaderboard data for the new session */
  liveLeaderboard?: LeaderboardState | null
}

// ─── Countdown timer ──────────────────────────────────────────────────────────
function useCountdown(scheduledAt: string | null): { mins: number; secs: number; total: number } {
  const [remaining, setRemaining] = useState(() =>
    scheduledAt ? Math.max(0, new Date(scheduledAt).getTime() - Date.now()) : 0
  )
  useEffect(() => {
    if (!scheduledAt) return
    const id = setInterval(() => {
      setRemaining(Math.max(0, new Date(scheduledAt).getTime() - Date.now()))
    }, 1000)
    return () => clearInterval(id)
  }, [scheduledAt])

  const totalSecs = Math.floor(remaining / 1000)
  return {
    total: remaining,
    mins: Math.floor(totalSecs / 60),
    secs: totalSecs % 60,
  }
}

// ─── Zone colour helper ───────────────────────────────────────────────────────
const ZONE_COLORS: Record<string, string> = {
  ZONE_1: '#8E9AAF',
  ZONE_2: '#3CC8D0',
  ZONE_3: '#8BD86A',
  ZONE_4: '#FF9640',
  ZONE_5: '#FF5C9A',
}

// ─── Left panel: leaderboard (live or final) ─────────────────────────────────
function FinalLeaderboard({
  session,
  leaderboard,
  isLive = false,
}: {
  session: LiveSession | null
  leaderboard: LeaderboardState | null
  isLive?: boolean
}) {
  const entries = leaderboard?.entries ?? []
  const className = session?.classInstance?.template?.name ?? 'Session'
  const scoringMode = leaderboard?.scoringMode ?? 'EFFORT_SCORE'

  const scoreLabel =
    scoringMode === 'CALORIES_ONLY' ? 'kcal'
    : scoringMode === 'HR_ZONE_POINTS' ? 'pts'
    : 'score'

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 px-6 pt-5 pb-3">
        <div className="flex items-center gap-3 mb-1">
          <div
            className="rounded-full px-2.5 py-0.5 text-[10px] font-black tracking-widest uppercase"
            style={
              isLive
                ? { background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', color: '#30D158' }
                : { background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#FF453A' }
            }
          >
            {isLive ? 'Live' : 'Final'}
          </div>
          <h2 className="text-white font-black text-xl truncate">{className}</h2>
        </div>
        <p className="text-white/20 text-xs uppercase tracking-widest">
          {entries.length} athletes · {scoreLabel}
        </p>
      </div>

      {/* Divider */}
      <div className="mx-6 h-px bg-white/[0.05] flex-shrink-0" />

      {/* List */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 pt-2 pb-4 space-y-1 scrollbar-none">
        <AnimatePresence initial={false}>
          {entries.map((entry, i) => (
            <FinalRow key={entry.athleteId} entry={entry} index={i} scoreLabel={scoreLabel} />
          ))}
        </AnimatePresence>

        {entries.length === 0 && (
          <div className="h-full flex items-center justify-center">
            <p className="text-white/20 text-sm">No results</p>
          </div>
        )}
      </div>
    </div>
  )
}

function FinalRow({
  entry,
  index,
  scoreLabel,
}: {
  entry: LeaderboardEntry
  index: number
  scoreLabel: string
}) {
  const rankColors = ['#f59e0b', '#B4B3C2', '#cd7c2a']
  const rankColor = index < 3 ? rankColors[index] : 'rgba(241, 238, 232, 0.15)'
  const zoneColor = ZONE_COLORS[entry.hrZone ?? ''] ?? 'transparent'

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className="flex items-center gap-3 rounded-xl px-3 py-2"
      style={{ background: 'rgba(241, 238, 232, 0.03)' }}
    >
      {/* Rank */}
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-black"
        style={{ background: `${rankColor}22`, color: rankColor }}
      >
        {entry.rank}
      </div>

      {/* Zone dot */}
      <div
        className="w-2 h-2 rounded-full flex-shrink-0"
        style={{ backgroundColor: zoneColor }}
      />

      {/* Name */}
      <p className="flex-1 text-white/80 text-sm font-semibold truncate">
        {entry.athleteName}
      </p>

      {/* Score */}
      <div className="text-right flex-shrink-0">
        <p className="text-white font-black text-sm tabular-nums">
          {Math.round(entry.score)}
        </p>
        <p className="text-white/20 text-[9px] uppercase">{scoreLabel}</p>
      </div>
    </motion.div>
  )
}

// ─── Right panel: upcoming session preview + check-in QR ─────────────────────
function UpcomingPanel({
  session,
  qrToken,
  qrSignature,
  qrExpiresAt,
  roster,
}: {
  session: LiveSession
  qrToken: string | null
  qrSignature: string | null
  qrExpiresAt: number | null
  roster: RosterEntry[]
}) {
  const scheduledAt = (session.classInstance as any)?.scheduledAt as string | null
  const { mins, secs, total } = useCountdown(scheduledAt)

  const className = session?.classInstance?.template?.name ?? 'Next Class'
  const coach = session?.classInstance?.coach as any
  const coachName = coach ? `${coach.firstName} ${coach.lastName}` : null

  const isImminent = total <= 60_000 // under 1 minute

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-6 pt-5 pb-3">
        <div className="flex items-center gap-2 mb-3">
          <div
            className="flex items-center gap-1.5 rounded-full px-3 py-1"
            style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)' }}
          >
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-indigo-400"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
            <span className="text-indigo-400 text-[10px] font-black tracking-widest uppercase">
              Up next
            </span>
          </div>
        </div>

        <h2
          className="text-white font-black leading-tight mb-1 truncate"
          style={{ fontSize: 'clamp(1.4rem, 2.5vw, 2rem)' }}
        >
          {className}
        </h2>
        {coachName && (
          <p className="text-white/35 text-xs mb-1">with {coachName}</p>
        )}

        {/* Countdown */}
        <motion.div
          className="font-black tabular-nums mt-2"
          style={{
            fontSize: 'clamp(2rem, 4vw, 3.5rem)',
            color: isImminent ? '#30D158' : 'rgba(255,255,255,0.85)',
            lineHeight: 1,
          }}
          animate={isImminent ? { scale: [1, 1.04, 1] } : {}}
          transition={{ duration: 0.8, repeat: Infinity }}
        >
          {mins}:{secs.toString().padStart(2, '0')}
        </motion.div>
        <p className="text-white/20 text-[9px] uppercase tracking-[0.2em] mt-0.5">
          {total === 0 ? 'Starting now' : 'until start'}
        </p>
      </div>

      {/* Divider */}
      <div className="mx-6 h-px bg-white/[0.05] flex-shrink-0" />

      {/* QR check-in */}
      <div className="flex-shrink-0 flex flex-col items-center py-4 px-4">
        <QrDisplay token={qrToken} signature={qrSignature} expiresAt={qrExpiresAt} />
      </div>

      {/* Divider */}
      <div className="mx-6 h-px bg-white/[0.05] flex-shrink-0" />

      {/* Checked-in roster */}
      <div className="flex-1 overflow-y-auto px-4 pt-2 pb-4 scrollbar-none">
        <p className="text-white/20 text-[9px] uppercase tracking-widest mb-2">
          Checked in · {roster.length}
        </p>
        {roster.length === 0 ? (
          <p className="text-white/15 text-xs text-center py-4">Scan to join</p>
        ) : (
          <div className="space-y-1">
            <AnimatePresence initial={false}>
              {roster.map((entry) => (
                <motion.div
                  key={entry.athleteId}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  layout
                  className="flex items-center gap-2 rounded-lg px-3 py-1.5"
                  style={{ background: 'rgba(241, 238, 232, 0.03)' }}
                >
                  <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-indigo-400 text-[10px] font-bold">
                      {entry.athleteName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-white/70 text-xs font-medium truncate flex-1">
                    {entry.athleteName}
                  </span>
                  <span className="text-green-400 text-[10px]">✓</span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Divider ──────────────────────────────────────────────────────────────────
function SplitDivider() {
  return (
    <div className="relative flex-shrink-0 w-px self-stretch mx-2">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/10 to-transparent" />
      {/* Animated glow bead */}
      <motion.div
        className="absolute left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-indigo-400"
        animate={{ top: ['5%', '95%', '5%'] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function SplitScreen({
  endedSession,
  endedLeaderboard,
  isLive = false,
  upcomingSession,
  upcomingQrToken = null,
  upcomingQrSignature = null,
  upcomingQrExpiresAt = null,
  upcomingRoster = [],
  liveSession,
  liveLeaderboard,
}: Props) {
  const dividerRef = useRef<HTMLDivElement>(null)

  // Handoff mode: liveSession is provided — right panel is the new class's live lb
  const isHandoff = !!liveSession

  return (
    <div className="w-screen h-screen bg-[#0E1022] flex overflow-hidden">
      {/* Screen badge */}
      <div className="absolute top-5 right-5 z-30">
        <ScreenBadge className="text-white/25 text-[10px]" />
      </div>

      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background: isHandoff
              ? // Handoff: left = red (ended), right = green (live)
                'radial-gradient(ellipse 40% 60% at 0% 50%, rgba(239,68,68,0.05) 0%, transparent 50%),' +
                'radial-gradient(ellipse 40% 60% at 100% 50%, rgba(34,197,94,0.05) 0%, transparent 50%)'
              : 'radial-gradient(ellipse 40% 60% at 0% 50%, rgba(239,68,68,0.04) 0%, transparent 50%),' +
                'radial-gradient(ellipse 40% 60% at 100% 50%, rgba(99,102,241,0.06) 0%, transparent 50%)',
          }}
        />
      </div>

      {/* ── Left panel: previous/current leaderboard (60%) ───────────────── */}
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 flex-[6] overflow-hidden"
      >
        <FinalLeaderboard session={endedSession} leaderboard={endedLeaderboard} isLive={isLive} />
      </motion.div>

      {/* ── Divider ───────────────────────────────────────────────────────── */}
      <SplitDivider />

      {/* ── Right panel ───────────────────────────────────────────────────── */}
      <motion.div
        ref={dividerRef}
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="relative z-10 flex-[4] overflow-hidden"
      >
        {isHandoff ? (
          // Handoff mode: show the new class's live leaderboard on the right
          <FinalLeaderboard session={liveSession} leaderboard={liveLeaderboard ?? null} isLive />
        ) : upcomingSession ? (
          // Normal mode: upcoming PRESTART with countdown + QR check-in
          <UpcomingPanel
            session={upcomingSession}
            qrToken={upcomingQrToken}
            qrSignature={upcomingQrSignature}
            qrExpiresAt={upcomingQrExpiresAt}
            roster={upcomingRoster}
          />
        ) : null}
      </motion.div>
    </div>
  )
}
